import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardAction } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface KpiCardProps {
  title: string;
  value: string | number | React.ReactNode;
  icon?: LucideIcon;
  description?: string;
  trend?: {
    value: number;
    label?: string;
    isPositive?: boolean;
  };
  className?: string;
  valueClassName?: string;
  iconClassName?: string;
  children?: React.ReactNode;
}

export function KpiCard({
  title,
  value,
  icon: Icon,
  description,
  trend,
  className,
  valueClassName,
  iconClassName,
  children,
}: KpiCardProps) {
  const getTrendIcon = () => {
    if (!trend) return null;
    
    if (trend.value === 0) {
      return <Minus className="h-3 w-3" />;
    }
    
    return trend.value > 0 ? (
      <TrendingUp className="h-3 w-3" />
    ) : (
      <TrendingDown className="h-3 w-3" />
    );
  };

  const getTrendColor = () => {
    if (!trend || trend.value === 0) return 'text-muted-foreground';
    
    // If isPositive is explicitly set, use that
    if (trend.isPositive !== undefined) {
      return trend.isPositive ? 'text-success-600' : 'text-destructive';
    }
    
    // Otherwise, positive numbers are good, negative are bad
    return trend.value > 0 ? 'text-success-600' : 'text-destructive';
  };

  const formatTrendValue = () => {
    if (!trend) return null;
    
    const prefix = trend.value > 0 ? '+' : '';
    const suffix = trend.label || '';
    
    return `${prefix}${trend.value}${suffix}`;
  };

  return (
    <Card className={cn('transition-all hover:shadow-md', className)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        {Icon && (
          <CardAction>
            <div className={cn('rounded-full bg-primary/10 p-2', iconClassName)}>
              <Icon className="h-4 w-4 text-primary" />
            </div>
          </CardAction>
        )}
      </CardHeader>
      <CardContent className="space-y-1">
        <div className={cn('text-2xl font-bold tracking-tight', valueClassName)}>
          {value}
        </div>
        
        {(description || trend) && (
          <div className="flex items-center gap-2 text-xs">
            {trend && (
              <span className={cn('flex items-center gap-1 font-medium', getTrendColor())}>
                {getTrendIcon()}
                {formatTrendValue()}
              </span>
            )}
            {description && (
              <>
                {trend && <span className="text-muted-foreground">•</span>}
                <span className="text-muted-foreground">{description}</span>
              </>
            )}
          </div>
        )}
        
        {children}
      </CardContent>
    </Card>
  );
}

// Variant for compact KPI cards
export function CompactKpiCard({
  title,
  value,
  icon: Icon,
  className,
  valueClassName,
}: Omit<KpiCardProps, 'description' | 'trend' | 'children'>) {
  return (
    <Card className={cn('transition-all hover:shadow-md', className)}>
      <CardContent className="flex items-center justify-between p-4">
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">{title}</p>
          <p className={cn('text-xl font-bold', valueClassName)}>{value}</p>
        </div>
        {Icon && (
          <div className="rounded-full bg-primary/10 p-2.5">
            <Icon className="h-5 w-5 text-primary" />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
